import { getCurrentInstance, nextTick, ref, Ref, set } from 'vue';
import CatalogV2Service from '@/api/services/catalog-v2.service';
import ItemsService from '@/api/services/items.service';
import { uploadMenuPhoto } from '@/composition/menu-photo-upload';
import { useConfirmDialog } from '@/composition/use-confirm-dialog';
import { useGtag } from '@/composition/use-gtag';
import { useMenuStore } from '@/composition/use-menu';
import { useUi } from '@/composition/use-ui';
import type { ICatalogAccess } from '@/domain/catalog/catalog-access';
import {
  CONVERSION_PRODUCT_COUNT,
  TMenuV2EventLabel,
} from '@/domain/catalog/catalog-analytics';
import { activePrices } from '@/domain/catalog/catalog-filters';
import {
  createCategoryPayload,
  emptyCategoryForm,
} from '@/domain/catalog/category-form';
import { storefrontItemUrl } from '@/domain/catalog/menu-links';
import { undoPriceChange } from '@/domain/catalog/price-form';
import {
  fireAdsConversion,
  FIVE_ITEMS_CONVERSION,
} from '@/domain/store-setup/store-setup-analytics';
import { AlertType } from '@/interfaces/IAlert';
import {
  IBulkMoveChange,
  IBulkPriceChange,
  IItemCreate,
  TCategoryDeleteItems,
} from '@/interfaces/ICatalogV2';
import { ICategory } from '@/interfaces/ICategory';
import { IItem } from '@/interfaces/IItem';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { getTopLevelDomain } from '@/utils/domain.utils';
import { vueData } from '@/VueData';
import type { ICatalogPageState } from './catalog-page-state';
import { useCatalogAvailability } from './use-catalog-availability';
import type { ICatalogData } from './use-catalog-data';
import type { ICatalogUrl } from './use-catalog-url';
import {
  IBulkOutcome,
  IUndoReport,
  useCatalogWrites,
} from './use-catalog-writes';

export interface ICatalogActionsDeps {
  state: ICatalogPageState;
  data: ICatalogData;
  access: Ref<ICatalogAccess>;
  online: Ref<boolean>;
  url: ICatalogUrl;
  /** Docked editor (desktop): opening another product switches it in place. */
  editorDocked: () => boolean;
}

const NO_OUTCOME: IBulkOutcome = { appliedIds: [] };

/**
 * The instant actions of the Menú page (plan §5.9 and §5.10): each one
 * writes through CatalogV2Service, upserts the response into the menu
 * store, pins the rows it touched, and offers «Deshacer» when it can be
 * undone (`use-catalog-writes.ts`). Availability lives in
 * `use-catalog-availability.ts` and is returned with the rest.
 */
export function useCatalogActions(deps: ICatalogActionsDeps) {
  const { state, data, access, online, url } = deps;
  const ui = useUi();
  const gtag = useGtag();
  const confirmDialog = useConfirmDialog();
  const menuStore = useMenuStore();
  const restaurantStore = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const tc = (key: string, count: number, params?: Record<string, unknown>) =>
    String(proxy.$tc(key, count, params));

  const writes = useCatalogWrites({
    state,
    restaurantId: () => data.restaurant.value?.id ?? null,
    online,
    isFiltering: () => data.isFiltering.value,
  });
  const availability = useCatalogAvailability({ state, data, access, writes });

  /** Closes the editor when what it shows was deleted. */
  function closeEditorOf(kind: 'item' | 'category', ids: readonly number[]) {
    const editor = state.editor;
    if (
      editor?.kind === kind &&
      typeof editor.id === 'number' &&
      ids.includes(editor.id)
    ) {
      // Gone: there is nothing left to keep, so closing asks nothing.
      state.editorDirty = false;
      url.closeEditor();
    }
  }

  function dropFromSelection(ids: readonly number[]) {
    if (!ids.length) return;
    const selected = { ...state.selectedIds };
    for (const id of ids) delete selected[id];
    state.selectedIds = selected;
  }

  /** A product the server no longer has: say so and drop it from the list. */
  function reportGone(itemIds: readonly number[]) {
    ui.showToast(AlertType.Error, t('menuPage.deeplink.itemGone'));
    void data.refreshItems([...itemIds]);
  }

  //#region Prices

  /** Price changes of one or many products, with the ifMatch undo. */
  async function writePrices(
    changes: readonly IBulkPriceChange[],
    label: TMenuV2EventLabel,
    success: (applied: number) => string
  ): Promise<IBulkOutcome> {
    const rid = writes.restaurantId();
    if (!rid || !changes.length) return NO_OUTCOME;
    const written = await writes.writeInChunks(
      changes,
      (part) => CatalogV2Service.bulkPrices(rid, part),
      (result) => result.before.map((before) => before.itemId)
    );
    if (!written || rid !== writes.restaurantId()) return NO_OUTCOME;
    const saved = written.results.flatMap((result) => result.items);
    menuStore.upsertItems(saved, { own: true });
    writes.pin(saved.map((item) => item.id));
    writes.track(label);

    const savedById = new Map(saved.map((item) => [item.id, item]));
    const undo = written.results
      .flatMap((result) => result.before)
      .flatMap((before) => {
        const after = savedById.get(before.itemId);
        return after ? [undoPriceChange(before, after)] : [];
      });
    writes.showResult(
      writes.bulkToast(changes.length, written, success),
      undo.length ? () => undoPrices(rid, undo) : null
    );
    return { appliedIds: written.appliedIds };
  }

  async function undoPrices(
    rid: number,
    undo: IBulkPriceChange[]
  ): Promise<IUndoReport> {
    const report: IUndoReport = { kept: 0, skipped: 0 };
    await writes.undoInChunks(
      undo,
      (part) => CatalogV2Service.bulkPrices(rid, part),
      (result) => {
        report.kept += result.keptIds.length;
        report.skipped += result.skippedIds.length;
        if (rid === writes.restaurantId())
          menuStore.upsertItems(result.items, { own: true });
      }
    );
    return report;
  }

  /**
   * «Cambiar precio» of one product. `reveal`: it was created hidden only
   * for lack of a price («Poner precio»), so a real price also shows it.
   */
  async function savePrice(
    change: IBulkPriceChange,
    options: { reveal?: boolean } = {}
  ): Promise<boolean> {
    const rid = writes.restaurantId();
    if (!rid) return false;
    const written = await writes.writeInChunks(
      [change],
      (part) => CatalogV2Service.bulkPrices(rid, part),
      (result) => result.before.map((before) => before.itemId)
    );
    if (!written || rid !== writes.restaurantId()) return false;
    const saved = written.results.flatMap((result) => result.items)[0];
    const before = written.results.flatMap((result) => result.before)[0];
    if (!saved || !before) {
      reportGone([change.itemId]);
      return false;
    }
    menuStore.upsertItems([saved], { own: true });
    writes.pin([saved.id]);
    writes.track('quick_price');
    const priceUndo = undoPriceChange(before, saved);

    // Showing it is a store-wide change: only roles that manage every branch.
    const reveal =
      options.reveal &&
      access.value.canHideEverywhere &&
      !saved.visible &&
      activePrices(saved).some((price) => Number(price) > 0);
    if (!reveal) {
      writes.showResult(
        { message: t('menuPage.toast.priceSaved'), type: AlertType.Success },
        () => undoPrices(rid, [priceUndo])
      );
      return true;
    }

    const shown = await writes.guarded(() =>
      CatalogV2Service.bulkAvailability(rid, [
        { itemId: saved.id, op: { visible: true } },
      ])
    );
    if (rid !== writes.restaurantId()) return true;
    if (shown) menuStore.upsertItems(shown.items, { own: true });
    writes.showResult(
      {
        message: t(
          shown ? 'menuPage.toast.pricedAndShown' : 'menuPage.toast.priceSaved',
          { name: saved.name }
        ),
        type: AlertType.Success,
      },
      async () => {
        if (shown) {
          const hidden = await CatalogV2Service.bulkAvailability(rid, [
            { itemId: saved.id, op: { visible: false } },
          ]);
          if (rid === writes.restaurantId())
            menuStore.upsertItems(hidden.items, { own: true });
        }
        return undoPrices(rid, [priceUndo]);
      }
    );
    return true;
  }

  /** «Cambiar precio de {n} productos». */
  function saveBulkPrices(changes: readonly IBulkPriceChange[]) {
    return writePrices(changes, 'bulk_price', (n) =>
      tc('menuPage.toast.bulkPrices', n, { n })
    );
  }

  /** Opens «Cambiar precio» on a row once it renders (after a quick add). */
  async function openPriceFor(itemId: number, reveal = false) {
    await nextTick();
    if (!data.itemById.value[itemId]) return;
    const anchor = document.querySelector<HTMLElement>(
      `[data-item-id="${itemId}"] .v2-catalog-price-field`
    );
    state.panel = { kind: 'price', targetId: itemId, anchor, reveal };
  }

  //#endregion

  //#region Photo and create

  /** Rows whose photo is uploading (the tile shows a spinner). */
  const photoBusyIds = ref<Record<number, true>>({});

  function setPhotoBusy(itemId: number, busy: boolean) {
    const next = { ...photoBusyIds.value };
    if (busy) next[itemId] = true;
    else delete next[itemId];
    photoBusyIds.value = next;
  }

  /** The picked photo is resized, uploaded and set as the product's picture. */
  async function uploadPhoto(item: IItem, file: File) {
    const rid = writes.restaurantId();
    if (!rid || photoBusyIds.value[item.id]) return;
    if (!online.value) {
      writes.showOffline();
      return;
    }
    const previous = item.picture || null;
    setPhotoBusy(item.id, true);
    try {
      let picture: string;
      try {
        picture = await uploadMenuPhoto(file);
      } catch (error) {
        ui.serverError(error, t('menuPage.errors.photo'));
        return;
      }
      if (rid !== writes.restaurantId()) return;
      const result = await writes.guarded(() =>
        CatalogV2Service.patchItem(item.id, { picture })
      );
      if (!result || rid !== writes.restaurantId()) return;
      const saved = result.item;
      menuStore.upsertItems([saved], { own: true });
      writes.pin([item.id]);
      writes.track('photo_tile');
      writes.showResult(
        {
          message: t(
            previous
              ? 'menuPage.toast.photoChanged'
              : 'menuPage.toast.photoAdded'
          ),
          type: AlertType.Success,
        },
        async () => {
          // Another device changed the photo meanwhile: theirs stays.
          const current = data.itemById.value[item.id];
          if (current && (current.picture || null) !== (saved.picture || null))
            return { kept: 1, skipped: 0 };
          const undone = await CatalogV2Service.patchItem(item.id, {
            picture: previous,
          });
          if (rid === writes.restaurantId())
            menuStore.upsertItems([undone.item], { own: true });
          return { kept: 0, skipped: 0 };
        }
      );
    } finally {
      setPhotoBusy(item.id, false);
    }
  }

  /** Legacy parity: the ads conversion when the store reaches five products. */
  async function trackProductCreated(rid: number) {
    try {
      const count = await new ItemsService().getCount(rid);
      if (count === CONVERSION_PRODUCT_COUNT)
        fireAdsConversion(
          FIVE_ITEMS_CONVERSION,
          vueData.conversion.bind(vueData)
        );
    } catch {
      // Telemetry must never break the page.
    }
  }

  /** The analytics of every product create (editor or quick add). */
  function trackCreate(rid: number) {
    gtag.event('create', { event_category: 'item' });
    void trackProductCreated(rid);
  }

  function trackCategoryCreate() {
    gtag.event('create', { event_category: 'category' });
  }

  /**
   * «Agregar producto a …» with a validated price; `null` (left empty)
   * creates it hidden.
   */
  async function quickAdd(
    category: ICategory,
    name: string,
    price: number | null
  ): Promise<IItem | null> {
    const rid = writes.restaurantId();
    const trimmed = name.trim();
    if (!rid || !trimmed) return null;
    const body: IItemCreate = {
      restaurantId: rid,
      categoryId: category.id,
      name: trimmed,
      price: price ?? 0,
    };
    if (price === null) body.availability = { visible: false };
    const result = await writes.guarded(() =>
      CatalogV2Service.createItem(body)
    );
    if (!result || rid !== writes.restaurantId()) return null;
    const created = result.item;
    menuStore.upsertItems([created], { own: true });
    writes.pin([created.id]);
    writes.track('quick_add');
    trackCreate(rid);
    if (price === null)
      writes.showAction(
        {
          message: t('menuPage.toast.createdHidden', { name: trimmed }),
          type: AlertType.Success,
        },
        {
          text: t('menuPage.toast.setPrice'),
          run: () => void openPriceFor(created.id, true),
        }
      );
    return created;
  }

  /**
   * A starter chip of the empty store (plan §5.15): the category is created
   * right away, printing on the default station, and opens in the editor.
   */
  async function createStarterCategory(name: string): Promise<boolean> {
    const rid = writes.restaurantId();
    if (!rid) return false;
    const form = {
      ...emptyCategoryForm(
        restaurantStore.printStations.map((station) => station.stationId)
      ),
      name,
    };
    const result = await writes.guarded(() =>
      CatalogV2Service.createCategory(
        createCategoryPayload(form, rid, state.menuId)
      )
    );
    if (!result || rid !== writes.restaurantId()) return false;
    menuStore.upsertCategories([result.category], { own: true });
    trackCategoryCreate();
    ui.showToast(AlertType.Success, t('menuPage.toast.categoryCreated'));
    state.focusQuickAddCategoryId = result.category.id;
    url.openCategory(result.category.id, deps.editorDocked());
    return true;
  }

  //#endregion

  //#region Descuentos

  /** The storefront's «Descuentos» switch, in the store and the legacy globals. */
  function applyShowDiscounts(show: boolean) {
    const settings = restaurantStore.restaurant?.settings;
    if (settings) set(settings, 'showDiscounts', show);
    if (vueData.restaurantSettings && vueData.restaurantSettings !== settings)
      set(vueData.restaurantSettings, 'showDiscounts', show);
  }

  async function writeShowDiscounts(rid: number, show: boolean) {
    const saved = await writes.guarded(() =>
      CatalogV2Service.patchSettings(rid, { showDiscounts: show })
    );
    if (!saved || rid !== writes.restaurantId()) return false;
    applyShowDiscounts(show);
    return true;
  }

  /** «Mostrar» on the Descuentos card (plan §5.16), with «Deshacer». */
  async function setShowDiscounts(show: boolean) {
    const rid = writes.restaurantId();
    if (!rid || !(await writeShowDiscounts(rid, show))) return;
    writes.track('discounts_switch');
    writes.showResult(
      {
        type: AlertType.Success,
        message: t(
          show ? 'menuPage.discounts.shownToast' : 'menuPage.discounts.hiddenToast'
        ),
      },
      async () =>
        (await writeShowDiscounts(rid, !show))
          ? { kept: 0, skipped: 0 }
          : null
    );
  }

  //#endregion

  //#region Lo más pedido

  /** The storefront's «Lo más pedido» switch, in the store and the legacy globals. */
  function applyShowPopular(show: boolean) {
    const settings = restaurantStore.restaurant?.settings;
    if (settings) set(settings, 'showPopular', show);
    if (vueData.restaurantSettings && vueData.restaurantSettings !== settings)
      set(vueData.restaurantSettings, 'showPopular', show);
  }

  async function writeShowPopular(rid: number, show: boolean) {
    const saved = await writes.guarded(() =>
      CatalogV2Service.patchSettings(rid, { showPopular: show })
    );
    if (!saved || rid !== writes.restaurantId()) return false;
    applyShowPopular(show);
    return true;
  }

  /** «Mostrar» on the «Lo más pedido» card (Storefront V2 D-ADM-4), with «Deshacer». */
  async function setShowPopular(show: boolean) {
    const rid = writes.restaurantId();
    if (!rid || !(await writeShowPopular(rid, show))) return;
    writes.track('popular_switch');
    writes.showResult(
      {
        type: AlertType.Success,
        message: t(
          show ? 'menuPage.popular.shownToast' : 'menuPage.popular.hiddenToast'
        ),
      },
      async () =>
        (await writeShowPopular(rid, !show)) ? { kept: 0, skipped: 0 } : null
    );
  }

  async function writeItemPopularHidden(
    rid: number,
    itemId: number,
    hidden: boolean
  ) {
    const result = await writes.guarded(() =>
      CatalogV2Service.patchItem(itemId, { popularHidden: hidden })
    );
    if (!result || rid !== writes.restaurantId()) return false;
    menuStore.upsertItems([result.item], { own: true });
    return true;
  }

  /** «Ocultar» / «Mostrar» of a product in the «Lo más pedido» list (owner 2026-11-03), with «Deshacer». */
  async function setItemPopularHidden(item: IItem, hidden: boolean) {
    const rid = writes.restaurantId();
    if (!rid || !(await writeItemPopularHidden(rid, item.id, hidden))) return;
    writes.pin([item.id]);
    writes.track('popular_item');
    writes.showResult(
      {
        type: AlertType.Success,
        message: t(
          hidden
            ? 'menuPage.popular.itemHiddenToast'
            : 'menuPage.popular.itemShownToast',
          { name: item.name }
        ),
      },
      async () =>
        (await writeItemPopularHidden(rid, item.id, !hidden))
          ? { kept: 0, skipped: 0 }
          : null
    );
  }

  async function writeCategoryPopularHidden(
    rid: number,
    categoryId: number,
    hidden: boolean
  ) {
    const result = await writes.guarded(() =>
      CatalogV2Service.patchCategory(categoryId, { popularHidden: hidden })
    );
    if (!result || rid !== writes.restaurantId()) return false;
    menuStore.upsertCategories([result.category], { own: true });
    return true;
  }

  /** «Toda la categoría» / «Mostrar» of a category in the «Lo más pedido» list, with «Deshacer». */
  async function setCategoryPopularHidden(
    category: ICategory,
    hidden: boolean
  ) {
    const rid = writes.restaurantId();
    if (
      !rid ||
      !(await writeCategoryPopularHidden(rid, category.id, hidden))
    )
      return;
    writes.track('popular_category');
    writes.showResult(
      {
        type: AlertType.Success,
        message: t(
          hidden
            ? 'menuPage.popular.categoryHiddenToast'
            : 'menuPage.popular.categoryShownToast',
          { name: category.name }
        ),
      },
      async () =>
        (await writeCategoryPopularHidden(rid, category.id, !hidden))
          ? { kept: 0, skipped: 0 }
          : null
    );
  }

  //#endregion

  //#region Duplicate, move, delete, view

  /** «Duplicar»: a hidden copy right under the original, opened in the editor. */
  async function duplicate(item: IItem) {
    const rid = writes.restaurantId();
    if (!rid) return;
    const name = t('menuPage.row.copyName', { name: item.name }).slice(0, 128);
    const result = await writes.guarded(() =>
      CatalogV2Service.duplicateItem(item.id, { name })
    );
    if (!result || rid !== writes.restaurantId()) return;
    // The server shifted the siblings after the original: mirror it now
    // (the realtime refresh confirms it).
    const shifted: Record<number, number> = {};
    for (const sibling of data.items.value)
      if (
        sibling.categoryId === item.categoryId &&
        sibling.id !== item.id &&
        sibling.index > item.index
      )
        shifted[sibling.id] = sibling.index + 1;
    menuStore.patchItemIndexes(shifted);
    menuStore.upsertItems([result.item], { own: true });
    writes.pin([result.item.id]);
    writes.track('duplicate');
    ui.showToast(
      AlertType.Success,
      t('menuPage.toast.duplicated', { name: item.name })
    );
    url.openItem(result.item.id, deps.editorDocked());
  }

  /** Moves products to the end of a same-menu category, with undo. */
  async function moveItems(
    items: readonly IItem[],
    target: ICategory
  ): Promise<IBulkOutcome> {
    const rid = writes.restaurantId();
    if (!rid || !items.length) return NO_OUTCOME;
    const changes: IBulkMoveChange[] = items.map((item) => ({
      itemId: item.id,
      categoryId: target.id,
    }));
    const written = await writes.writeInChunks(
      changes,
      (part) => CatalogV2Service.bulkMove(rid, part),
      (result) => result.before.map((before) => before.itemId)
    );
    if (!written || rid !== writes.restaurantId()) return NO_OUTCOME;
    const saved = written.results.flatMap((result) => result.items);
    menuStore.upsertItems(saved, { own: true });
    writes.pin(saved.map((item) => item.id));
    writes.track(items.length > 1 ? 'bulk_move' : 'move');

    const savedById = new Map(saved.map((item) => [item.id, item]));
    const undo: IBulkMoveChange[] = written.results
      .flatMap((result) => result.before)
      .flatMap((before) => {
        const after = savedById.get(before.itemId);
        return after
          ? [
              {
                itemId: before.itemId,
                categoryId: before.categoryId,
                index: before.index,
                ifMatch: { categoryId: after.categoryId, index: after.index },
              },
            ]
          : [];
      });
    const success = (n: number) =>
      items.length === 1
        ? t('menuPage.toast.moved', {
            name: items[0].name,
            category: target.name,
          })
        : tc('menuPage.toast.bulkMoved', n, { n, category: target.name });
    writes.showResult(
      writes.bulkToast(items.length, written, success),
      undo.length
        ? async () => {
            const report: IUndoReport = { kept: 0, skipped: 0 };
            await writes.undoInChunks(
              undo,
              (part) => CatalogV2Service.bulkMove(rid, part),
              (result) => {
                report.kept += result.keptIds.length;
                report.skipped += result.skippedIds.length;
                if (rid === writes.restaurantId())
                  menuStore.upsertItems(result.items, { own: true });
              }
            );
            return report;
          }
        : null
    );
    return { appliedIds: written.appliedIds };
  }

  /** Asks, then deletes (no undo: past sales keep their history). */
  async function deleteItems(items: readonly IItem[]): Promise<IBulkOutcome> {
    const rid = writes.restaurantId();
    if (!rid || !items.length) return NO_OUTCOME;
    const single = items.length === 1;
    const confirmed = await confirmDialog.confirm({
      title: single
        ? t('menuPage.delete.title', { name: items[0].name })
        : tc('menuPage.delete.titleMany', items.length, { n: items.length }),
      message: t(
        single ? 'menuPage.delete.message' : 'menuPage.delete.messageMany'
      ),
      acceptText: single
        ? t('menuPage.delete.accept')
        : tc('menuPage.delete.acceptMany', items.length, { n: items.length }),
      tone: 'danger',
    });
    if (!confirmed || rid !== writes.restaurantId()) return NO_OUTCOME;
    const written = await writes.writeInChunks(
      items.map((item) => item.id),
      (part) => CatalogV2Service.bulkDelete(rid, part),
      (result) => result.deletedIds
    );
    if (!written || rid !== writes.restaurantId()) return NO_OUTCOME;
    const deletedIds = written.appliedIds;
    // Skipped products no longer exist on the server either.
    const goneIds = written.stopped ? deletedIds : items.map((item) => item.id);
    menuStore.removeItems(goneIds, { own: true });
    dropFromSelection(deletedIds);
    if (!single) writes.track('bulk_delete');
    writes.showResult(
      writes.bulkToast(items.length, written, (n) =>
        single
          ? t('menuPage.toast.deleted')
          : tc('menuPage.toast.bulkDeleted', n, { n })
      ),
      null
    );
    closeEditorOf('item', deletedIds);
    return { appliedIds: deletedIds };
  }

  /**
   * Deletes a category, moving its products to another one or deleting
   * them too, in one transaction (no undo).
   */
  async function deleteCategory(
    category: ICategory,
    items: TCategoryDeleteItems
  ): Promise<boolean> {
    const rid = writes.restaurantId();
    if (!rid) return false;
    const target =
      items.items === 'move' ? data.categoryById.value.get(items.to) : null;
    const result = await writes.guarded(() =>
      CatalogV2Service.deleteCategory(category.id, items)
    );
    if (!result || rid !== writes.restaurantId()) return false;
    menuStore.removeItems(result.deletedItemIds, { own: true });
    menuStore.upsertItems(result.items, { own: true });
    menuStore.removeCategories([category.id], { own: true });
    dropFromSelection(result.deletedItemIds);
    writes.track(
      items.items === 'move' ? 'category_delete:move' : 'category_delete:delete'
    );
    const moved = result.items.length;
    const deleted = result.deletedItemIds.length;
    ui.showToast(
      AlertType.Success,
      moved && target
        ? tc('menuPage.toast.categoryDeletedMoved', moved, {
            n: moved,
            category: target.name,
          })
        : deleted
        ? tc('menuPage.toast.categoryDeletedWithItems', deleted, { n: deleted })
        : t('menuPage.toast.categoryDeleted')
    );
    closeEditorOf('category', [category.id]);
    closeEditorOf('item', result.deletedItemIds);
    return true;
  }

  /** «Ver en la carta»: the storefront's product page in a new tab. */
  function viewInMenu(item: IItem) {
    const stringId = data.restaurant.value?.stringId;
    if (!stringId) return;
    window.open(
      storefrontItemUrl(getTopLevelDomain(), stringId, item.id),
      '_blank',
      'noopener'
    );
  }

  //#endregion

  return {
    ...availability,
    savePrice,
    saveBulkPrices,
    openPriceFor,
    photoBusyIds,
    uploadPhoto,
    quickAdd,
    trackCreate,
    trackCategoryCreate,
    duplicate,
    moveItems,
    deleteItems,
    deleteCategory,
    createStarterCategory,
    viewInMenu,
    setShowDiscounts,
    setShowPopular,
    setItemPopularHidden,
    setCategoryPopularHidden,
    guarded: writes.guarded,
    showResult: writes.showResult,
    pin: writes.pin,
    track: writes.track,
  };
}

export type ICatalogActions = ReturnType<typeof useCatalogActions>;
